import type {
  IntegrationPhaseMapping,
  PhaseItem,
} from "../../../../pages/hid-admin/dashboard/partner-settings/technology-management/types";
import type {
  IntegrationDetailApiItem,
  IntegrationDetailUi,
  IntegrationPayloadType,
  IntegrationPhaseMappingApiItem,
} from "./Types";

const toBoolean = (value: boolean | number) =>
  value === true || value === 1;

const buildActivityDescriptionMap = (phases: PhaseItem[]) => {
  const descriptionMap = new Map<string, string>();
  phases.forEach((phase) => {
    phase.activities.forEach((activity) => {
      descriptionMap.set(activity.id, activity.description || "");
    });
  });
  return descriptionMap;
};

export const mapPhasesToIntegrationPhaseMappings = (
  phases: PhaseItem[],
  mappedDefault = false
): IntegrationPhaseMapping[] =>
  [...phases]
    .sort((a, b) => a.order - b.order)
    .map((phase, index) => ({
      id: phase.id,
      order: phase.order || index + 1,
      name: phase.name,
      activities: phase.activities.map((activity) => ({
        id: activity.id,
        name: activity.name,
        description: activity.description || "",
        mapped: mappedDefault,
      })),
    }));

const mapPhaseMappingApiToUi = (
  phase: IntegrationPhaseMappingApiItem,
  index: number,
  activityDescriptionMap: Map<string, string>
): IntegrationPhaseMapping => ({
  id: String(phase.phase_id),
  order: index + 1,
  name: phase.phase_name,
  activities: (phase.activities || []).map((activity) => ({
    id: String(activity.activity_id),
    name: activity.activity_name,
    description:
      activityDescriptionMap.get(String(activity.activity_id)) || "",
    mapped: toBoolean(activity.mapped),
  })),
});

export const mapIntegrationDetailApiToUi = (
  data: IntegrationDetailApiItem,
  phases: PhaseItem[] = []
): IntegrationDetailUi => {
  const activityDescriptionMap = buildActivityDescriptionMap(phases);

  return {
    name: data.integration_name || "",
    description: data.description || "",
    status: data.status === 1,
    phaseMappings: (data.phase_activity_mapping || []).map((phase, index) =>
      mapPhaseMappingApiToUi(phase, index, activityDescriptionMap)
    ),
  };
};

export const mapIntegrationFormToApiPayload = (data: {
  name: string;
  description: string;
  status: boolean;
  phaseMappings: IntegrationPhaseMapping[];
}): IntegrationPayloadType => ({
  integration_name: data.name.trim(),
  description: data.description,
  status: data.status ? 1 : 0,
  phase_activity_mapping: data.phaseMappings.flatMap((phase) =>
    phase.activities
      .filter((activity) => activity.mapped)
      .map((activity) => ({
        activity_id: Number(activity.id),
      }))
  ),
});
